import { TenderState, SpoilReconciliationData } from "../types/tender.types";
import { deriveOverridesFromSavedLibrary, buildPlotDrivesLibraryFromOverrides } from "./plotDrivesCodeOverrides";
import groundworksRatesMapping from "./groundworks_rates_mapping.json";
import { calculateAllTenderTotals } from "./summaryCalculations";
import { calculateBoqTenderTotal } from "./boqTotals";
import { getCurrentUserDisplayName } from "../hooks/useAuth";
import { deserializeDynamicBillSummaryTabs, deserializeCutFillsExtraItems, deserializeDrainageExtraItems } from "./dynamicBillSummarySerialization";
// Roads & Sewers' own fixed-summary-key filtering (kept separate from the
// import above so touching Roads & Sewers' Dynamic Bill Summary support can
// never change groundworks' own dynamicBillSummarySerialization.ts behaviour —
// see that file's own doc comment).
import { deserializeRoadsAndSewersDynamicBillSummaryTabs } from "./roadsAndSewersDynamicBillSummarySerialization";

const STORAGE_KEY = "mjEvans_tender_draft";
const STORAGE_VERSION = "3.0";

// Payload format interface based on payload.json
export interface PayloadFormat {
  project: {
    name: string;
    client: string;
    region: string;
  };
  tenderCreatedDate: string;
  createdBy?: string;
  calculatedAt?: string;
  lastModified: string;
  status: string;
  boqStatus?: string;
  opportunityNo?: string;
  opportunity?: {
    groundworks?: { no?: number; clientName: string; region: string; name: string; siteAddress: string };
    roadsAndSewers?: { no?: number; clientName: string; region: string; name: string; siteAddress: string };
  };
  groundworks?: any;
  roadsAndSewers?: any;
  globalSettings?: {
    roadsAndSewers: {
      defaultPipeType: "Plastic" | "Clay" | "Concrete";
      defaultBackfillType: "Type 1" | "6F2";
      overheadAndProfit: number;
      preliminaries: number;
      discount: number;
    };
    groundworks: {
      overheadAndProfit: number;
      preliminaries: number;
      discount: number;
    };
  };
  billOfQuantities?: any;
  totals?: any;
}

// Transform TenderState to PayloadFormat (remove unnecessary UI state)
// IMPORTANT: Only include keys that exist in payload.json - no extra nested structures!
export const transformToPayload = (
  tender: TenderState,
  createdBy?: string
): PayloadFormat => {
  // Extract ONLY the table data and metadata that exists in payload.json
  const groundworksPayload = tender.groundworks
    ? {
        // Tables (arrays of data rows)
        cutFillsTable: tender.groundworks.cutFillsTable || [],
        foundationsTable: (
          tender.groundworks.foundationsTable ||
          tender.groundworks.foundations ||
          []
        ).map((row: any) => {
          const prefix = row.plotType === 'garage' ? 'G-' : 'P-';
          const plotNo = String(row.plotNo ?? '');
          const normalizedPlotNo =
            plotNo === '' || plotNo.startsWith('P-') || plotNo.startsWith('G-')
              ? plotNo
              : prefix + plotNo;
          return normalizedPlotNo === plotNo ? row : { ...row, plotNo: normalizedPlotNo };
        }),
        gardenTable: tender.groundworks.gardenTable || [],
        plotSlabsTable: tender.groundworks.plotPavingsTable || [],
        scaffoldTable: (tender.groundworks.scaffoldTable || []).map((row: any) => {
          const prefix = row.plotType === 'garage' ? 'G-' : 'P-';
          const plotNo = String(row.plotNo ?? '');
          const normalizedPlotNo =
            plotNo === '' || plotNo.startsWith('P-') || plotNo.startsWith('G-')
              ? plotNo
              : prefix + plotNo;
          return normalizedPlotNo === plotNo ? row : { ...row, plotNo: normalizedPlotNo };
        }),
        plotServiceTable: tender.groundworks.plotServiceTable || [],
        plotServiceConfig: (tender.groundworks as any).plotServiceConfig,
        retainingWallsTable: tender.groundworks.retainingWallsTable || [],
        wallsBuildUpData: (tender.groundworks as any).wallsBuildUpData || {},
        plotDrivesTable: tender.groundworks.plotDrivesTable || [],
        drainageTable: tender.groundworks.drainageTable || [],
        drainageTableStandard:
          (tender.groundworks as any).drainageTableStandard || [],
        geoToberWallTable: tender.groundworks.geoToberWallTable || [],
        geoToberWallBuildUpData: (tender.groundworks as any).geoToberWallBuildUpData || {},
        geoToberWallBuildUpDimensions: (tender.groundworks as any).geoToberWallBuildUpDimensions || {},
        geoToberWallColumnHeaders: (tender.groundworks as any).geoToberWallColumnHeaders || {},
        geoToberWallDimensionValues: (tender.groundworks as any).geoToberWallDimensionValues || {},

        // Column headers and dimension values (metadata only - no nested structures)
        gardenColumnHeaders: (tender.groundworks as any).gardenColumnHeaders,
        gardenDimensionValues: (tender.groundworks as any)
          .gardenDimensionValues,
        plotSlabsColumnHeaders: (tender.groundworks as any)
          .plotPavingsColumnHeaders,
        plotSlabsDimensionValues: (tender.groundworks as any)
          .plotPavingsDimensionValues,
        scaffoldColumnHeaders: (tender.groundworks as any)
          .scaffoldColumnHeaders,
        scaffoldDimensionValues: (tender.groundworks as any)
          .scaffoldDimensionValues,
        plotDrivesColumnHeaders: (tender.groundworks as any)
          .plotDrivesColumnHeaders,
        plotDrivesDimensionValues: (tender.groundworks as any)
          .plotDrivesDimensionValues,

        // Drainage column headers and dimension values (dynamic headers for OSMA/Polypipe Storm tabs)
        drainageColumnHeaders: (tender.groundworks as any).drainageColumnHeaders,
        drainageDimensionValues: (tender.groundworks as any).drainageDimensionValues,

        // Drainage OSMA config (Nr Plots Take off & Total Plots from configure modal)
        drainageOSMAConfig: (() => {
          const dimValues = (tender.groundworks as any).drainageDimensionValues || {};
          return {
            nrPlotsTakeOff: dimValues.foulNrPlotsTakeOff ?? "",
            totalPlots: dimValues.foulTotalPlots ?? "",
          };
        })(),

        // Drainage Polypipe config (Nr Plots Take off & Total Plots from configure modal)
        drainagePolypipeConfig: (() => {
          const config = (tender.groundworks as any).drainagePolypipeConfig || {};
          return {
            nrPlotsTakeOff: config.nrPlotsTakeOff ?? "",
            totalPlots: config.totalPlots ?? "",
          };
        })(),

        // Mesh reinforcement type for dynamic headers
        meshReinforcementType: tender.groundworks.meshReinforcementType,

        // Foundations prefix values for dynamic concrete type headers
        foundationsPrefixValues: (tender.groundworks as any)
          .foundationsPrefixValues,

        // Cut & Fills prefix values for dynamic column headers
        cutFillsPrefixValues: (tender.groundworks as any)
          .cutFillsPrefixValues,

        // Substructure block type for dynamic headers
        substructureBlockType: (tender.groundworks as any)
          .substructureBlockType,

        // Topsoil table (user-editable onsite/offsite values)
        topsoilTableData: (tender.groundworks as any).topsoilTableData,

        // Topsoil Management configuration (Site Clearance Configure Headers)
        topsoilManagement: (tender.groundworks as any).topsoilManagement,

        // Subsoil Management configuration (Soil Management Configure Headers)
        subsoilManagement: (tender.groundworks as any).subsoilManagement,

        // Soil Management summary data (mapped from GW C&F Summary sheet)
        soilManagementData: (tender.groundworks as any).soilManagementData,

        // DO NOT include: cutFills, externalWorks, privateDrainage, additionalItems, matrix, plotMatrix, summarySheet, etc.
        // Those are internal structures, not part of the payload.json format
      }
    : undefined;

  // Extract roads and sewers data — every domain follows a { table, config } shape;
  // build-up cost tables (pump station, rising main, storm attenuation) are their own
  // root keys instead of nesting inside foulSewers/stormSewers.
  const roadsAndSewersPayload = {
    foulSewers: {
      manholes: tender.foulSewers?.manholes || { table: [], config: {} },
      pipeRuns: tender.foulSewers?.pipeRuns || { table: [], config: {} },
      lateralConnections:
        tender.foulSewers?.lateralConnections ||
        { table: [], config: { lateralsCount: 0, gulliesCount: 0 } },
    },
    pumpStation: tender.pumpStation || { table: {}, config: {} },
    risingMain: tender.risingMain || { table: {}, config: {} },
    stormSewers: {
      manholes: tender.stormSewers?.manholes || { table: [], config: {} },
      pipeRuns: tender.stormSewers?.pipeRuns || { table: [], config: { roadGulliesCount: 0 } },
      lateralConnections: tender.stormSewers?.lateralConnections || { table: [], config: {} },
      gullyConnections:
        tender.stormSewers?.gullyConnections ||
        { table: [], config: { gullyConnectionLength: 6 } },
    },
    boxCulverts: tender.boxCulverts || { table: {}, config: {} },
    cellularAttenuation: tender.cellularAttenuation || { table: {}, config: {} },
    concAttenuation: tender.concAttenuation || { table: {}, config: {} },
    pondsSwales: tender.pondsSwales || { table: {}, config: {} },
    weholiteAttenuation: tender.weholiteAttenuation || { table: {}, config: {} },
    roads: {
      roadTypes: tender.roads?.roadTypes || { table: [], config: {} },
      cutFillSections: tender.roads?.cutFillSections || { table: [], config: { stripValueDefault: 0 } },
      carriagewayFootways: tender.roads?.carriagewayFootways || { table: [], config: { totalBuildUpValue: 0 } },
      capping:
        tender.roads?.capping ||
        { table: [], config: { cappingLayer: { enabled: false, thicknesses: [] } } },
      footpaths: tender.roads?.footpaths || { table: [], config: {} },
      manualQtys: tender.roads?.manualQtys || {},
    },
    mainServices: tender.mainServices || { table: {}, config: {} },
    substation: tender.substation || { table: {}, config: {} },
    pos: tender.pos || { table: {}, config: {} },
    compound: tender.compound || { table: {}, config: {} },
    spoilReconciliation: tender.spoilReconciliation || { config: {} },
    manualPreliminaryItems: tender.preliminaryItems?.manualItems || [],
    // Preliminary Items page's own Main Services connections (unrelated to the
    // `mainServices` trenching domain above) — kept under a distinct key to avoid collision.
    mainServiceConnections: tender.preliminaryItems?.mainServices || [],
    preliminaryItems: (tender.preliminaryItems?.preliminaryItems || []).filter((i) => (i.quantity ?? 0) > 0),
  };

  // Extract global settings in new format
  const globalSettingsPayload = {
    roadsAndSewers: {
      defaultPipeType:
        tender.globalSettings?.pipeType === "plastic"
          ? "Plastic"
          : tender.globalSettings?.pipeType === "clay"
          ? "Clay"
          : "Concrete",
      defaultBackfillType:
        tender.globalSettings?.backfillType === "type1" ? "Type 1" : "6F2",
      overheadAndProfit:
        (tender.globalSettings as any)?.roadsAndSewers?.overheadAndProfit || 0,
      preliminaries:
        (tender.globalSettings as any)?.roadsAndSewers?.preliminaries || 0,
      discount: (tender.globalSettings as any)?.roadsAndSewers?.discount || 0,
    },
    groundworks: {
      overheadAndProfit:
        (tender.globalSettings as any)?.groundworks?.overheadAndProfit || 0,
      preliminaries:
        (tender.globalSettings as any)?.groundworks?.preliminaries || 0,
      discount: (tender.globalSettings as any)?.groundworks?.discount || 0,
      // TODO: Rename this key to 'discountAdded'
      discountAdded:
        tender.groundworks?.adjustments?.includeDiscount &&
        (tender.groundworks?.adjustments?.discount || 0) > 0
          ? "true"
          : "false",
    },
  };

  // Use values from state if already calculated (from hook), otherwise calculate.
  // `groundworksTotal` here is always the groundworks-domain-only figure that feeds
  // `boqEntry.groundworks.totals.grandTotal` below — it must never be combined with
  // `roadsAndSewersTotal`, or a Roads & Sewers-only save (where groundworks has no
  // hook-calculated totals yet) would leak the R&S total into the groundworks total.
  let groundworksTotal: number;
  let roadsAndSewersTotal: number;

  // R&S total: prefer the live Sum Analysis TENDER TOTAL (written to state by
  // RsSumAnalysisBillSummary.tsx) over the separate calculateAllTenderTotals estimate.
  const sumAnalysisGrandTotal = tender.roadsAndSewersTotals?.grandTotal;

  // Check if groundworks totals are already set in state (from hook calculations)
  if (tender.groundworks?.totals?.tenderTotal !== undefined) {
    // Use hook-calculated value from state — the FINAL groundworks-only total with
    // all its own adjustments (prelims/OHP/discount) applied.
    groundworksTotal = tender.groundworks.totals.tenderTotal;

    roadsAndSewersTotal =
      sumAnalysisGrandTotal ?? calculateAllTenderTotals(tender).roadsAndSewersTotal;
  } else {
    // Fallback: calculate totals independently — each domain keeps its own total.
    const calculatedTotals = calculateAllTenderTotals(tender);
    groundworksTotal = calculatedTotals.groundworksTotal;
    roadsAndSewersTotal = sumAnalysisGrandTotal ?? calculatedTotals.roadsAndSewersTotal;
  }

  // Check if R&S job type is selected (for conditional payload structure)
  const hasRoadsAndSewers = tender.jobTypes?.roadsAndSewers === true;

  const boqEntry = {
    // Only include R&S totals if that job type is selected
    roadAndSewers: hasRoadsAndSewers || roadsAndSewersTotal > 0
      ? {
          totals: {
            grandTotal: roadsAndSewersTotal,
          },
        }
      : {},
    groundworks: {
      totals: {
        grandTotal: groundworksTotal,
      },
      ohp: (tender.globalSettings as any)?.groundworks?.overheadAndProfit || 0,
      preliminaries: (tender.globalSettings as any)?.groundworks?.preliminaries || 0,
    },
  };

  return {
    project: {
      name: tender.projectName || "",
      client: tender.client || "",
      region: tender.siteAddress
        ? `${tender.siteAddress}~~${tender.region || "West Midlands"}`
        : tender.region || "West Midlands",
    },
    tenderCreatedDate:
      tender.dateCreated?.toISOString() || new Date().toISOString(),
    // Preserve existing creator if it exists, otherwise use logged-in user -
    // getCurrentUserDisplayName() reads straight from the MSAL account cache
    // as a last-resort fallback (instead of a hardcoded "User" string) for
    // any caller that didn't pass `createdBy` explicitly, or called before
    // its own useAuth() state had synced.
    createdBy: tender.createdBy || createdBy || getCurrentUserDisplayName() || "User",
    calculatedAt:
      tender.calculationResults?.calculatedAt?.toISOString() ||
      new Date().toISOString(),
    lastModified:
      tender.lastModified?.toISOString() || new Date().toISOString(),
    // boqStatus always takes precedence — status mirrors it so both fields stay in sync.
    ...((): { status: string; boqStatus: string } => {
      const raw = (tender.boqStatus || tender.status) as string;
      const effective = !raw || raw === 'Pending' ? 'In-progress' : raw;
      return { status: effective, boqStatus: effective };
    })(),
    "job-types": {
      groundworks: tender.jobTypes?.groundworks ?? false,
      roadsAndSewers: tender.jobTypes?.roadsAndSewers ?? false,
    },
    groundworks: groundworksPayload,
    roadsAndSewers: roadsAndSewersPayload,
    globalSettings: globalSettingsPayload,
    billOfQuantities: [boqEntry],
    // Always the sum of billOfQuantities[0].groundworks.totals.grandTotal and
    // .roadAndSewers.totals.grandTotal — see calculateBoqTenderTotal (src/utils/boqTotals.ts).
    totals: {
      tenderTotal: calculateBoqTenderTotal(boqEntry),
    },
    ...(tender.opportunityNo && { opportunityNo: tender.opportunityNo }),
    ...(tender.opportunity && { opportunity: tender.opportunity }),
  };
};

/**
 * Guards against pre-restructure flat-shape data (plain arrays, or objects missing
 * `table`/`config`) leaking through as the new `{ table, config }` wrapper — old saved
 * projects/localStorage drafts predate this shape and must fall back to defaults
 * instead of being passed through (which would crash consumers expecting `.table`/`.config`).
 */
const asShaped = <T>(value: unknown, fallback: T, requiredKey: 'table' | 'config' = 'table'): T =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && requiredKey in value
    ? (value as T)
    : fallback;

// Transform PayloadFormat back to TenderState (add back necessary UI state)
export const transformFromPayload = (
  payload: PayloadFormat,
  existingState?: Partial<TenderState>
): TenderState => {
  // Reconstruct groundworks data from tables - restore all tables directly to groundworks object
  const groundworksData = payload.groundworks
    ? {
        plots: [],
        foundations: payload.groundworks.foundationsTable || [],
        // Store tables directly on groundworks object
        cutFillsTable: payload.groundworks.cutFillsTable || [],
        foundationsTable: payload.groundworks.foundationsTable || [],
        gardenTable: payload.groundworks.gardenTable || [],
        plotPavingsTable: (payload.groundworks as any).plotSlabsTable || [],
        scaffoldTable: payload.groundworks.scaffoldTable || [],
        plotServiceTable: payload.groundworks.plotServiceTable || [],
        plotServiceConfig: (payload.groundworks as any).plotServiceConfig,
        retainingWallsTable: payload.groundworks.retainingWallsTable || [],
        wallsBuildUpData: (payload.groundworks as any).wallsBuildUpData || {},
        plotDrivesTable: payload.groundworks.plotDrivesTable || [],
        drainageTable: payload.groundworks.drainageTable || [],
        drainageTableStandard: payload.groundworks.drainageTableStandard || [],
        geoToberWallTable: payload.groundworks.geoToberWallTable || [],
        geoToberWallBuildUpData: (payload.groundworks as any).geoToberWallBuildUpData || {},
        geoToberWallBuildUpDimensions: (payload.groundworks as any).geoToberWallBuildUpDimensions || {},
        geoToberWallColumnHeaders: (payload.groundworks as any).geoToberWallColumnHeaders || {},
        geoToberWallDimensionValues: (payload.groundworks as any).geoToberWallDimensionValues || {},
        // Restore column headers and dimension values
        gardenColumnHeaders: payload.groundworks.gardenColumnHeaders,
        gardenDimensionValues: payload.groundworks.gardenDimensionValues,
        plotPavingsColumnHeaders: (payload.groundworks as any)
          .plotSlabsColumnHeaders,
        plotPavingsDimensionValues: (payload.groundworks as any)
          .plotSlabsDimensionValues,
        scaffoldColumnHeaders: payload.groundworks.scaffoldColumnHeaders,
        scaffoldDimensionValues: payload.groundworks.scaffoldDimensionValues,
        plotDrivesColumnHeaders: payload.groundworks.plotDrivesColumnHeaders,
        plotDrivesDimensionValues:
          payload.groundworks.plotDrivesDimensionValues,
        // Saved picks live only in the BOQ's groundworksItemLibrary — re-derive them
        plotDrivesCodeOverrides: deriveOverridesFromSavedLibrary(
          (payload as { groundworksItemLibrary?: { plotDrives?: unknown } }).groundworksItemLibrary?.plotDrives,
          payload.groundworks.plotDrivesDimensionValues,
        ),
        // Restore drainage column headers and dimension values
        drainageColumnHeaders: (payload.groundworks as any).drainageColumnHeaders,
        // Reverse-map drainageOSMAConfig → drainageDimensionValues
        drainageDimensionValues: (() => {
          const existing = (payload.groundworks as any).drainageDimensionValues || {};
          const osmaConfig = (payload.groundworks as any).drainageOSMAConfig || {};
          return {
            ...existing,
            ...(osmaConfig.nrPlotsTakeOff !== undefined ? { foulNrPlotsTakeOff: osmaConfig.nrPlotsTakeOff } : {}),
            ...(osmaConfig.totalPlots !== undefined ? { foulTotalPlots: osmaConfig.totalPlots } : {}),
          };
        })(),
        // Restore drainagePolypipeConfig directly
        drainagePolypipeConfig: (payload.groundworks as any).drainagePolypipeConfig || {},
        // Restore mesh reinforcement type
        meshReinforcementType: payload.groundworks.meshReinforcementType,
        // Restore foundations prefix values
        foundationsPrefixValues: payload.groundworks.foundationsPrefixValues,
        // Restore cut & fills prefix values
        cutFillsPrefixValues: (payload.groundworks as any).cutFillsPrefixValues,
        // Restore substructure block type
        substructureBlockType: payload.groundworks.substructureBlockType,
        // Restore Topsoil table (user-editable onsite/offsite)
        topsoilTableData: (payload.groundworks as any).topsoilTableData,
        // Restore Topsoil Management configuration
        topsoilManagement: (payload.groundworks as any).topsoilManagement,
        // Restore Subsoil Management configuration
        subsoilManagement: (payload.groundworks as any).subsoilManagement,
        // Restore Soil Management summary data
        soilManagementData: (payload.groundworks as any).soilManagementData,
        cutFills: payload.groundworks.cutFills || {
          paths: payload.groundworks.cutFillsTable || [],
          gardens: [],
          privateDrives: [],
          sharedDrives: [],
          drainageComponents: [],
          settings: {
            privateTarmacValue: 0,
            privateBlockValue: 0,
            sharedTarmacValue: 0,
            sharedBlockValue: 0,
          },
        },
        externalWorks: payload.groundworks.externalWorks || {
          settings: {
            gardenMeasurementMethod: "individual" as const,
            defaultTopsoilDepth: 150,
            standardDriveSpec: "standard_block_paving",
            topsoilStripDepth: 300,
            topsoilImportRate: 35.0,
            topsoilExportRate: 12.0,
            privateTarmacValue: 0,
            privateBlockValue: 0,
            sharedTarmacValue: 0,
            sharedBlockValue: 0,
          },
          specifications: [
            {
              id: "standard_block_paving",
              name: "Standard Block Paving",
              surfaceType: "block_paving" as const,
              surfaceThickness: 60,
              bedding: { material: "sharp_sand" as const, thickness: 50 },
              subBase: { material: "type1" as const, thickness: 200 },
              totalDepth: 310,
              isStandard: true,
            },
            {
              id: "standard_tarmac",
              name: "Standard Tarmac",
              surfaceType: "tarmac" as const,
              surfaceThickness: 40,
              binderCourse: { thickness: 60 },
              subBase: { material: "type1" as const, thickness: 200 },
              totalDepth: 300,
              isStandard: true,
            },
          ],
          privateDrives: payload.groundworks.plotDrivesTable || [],
          sharedDrives: [],
          paths: [],
          gardens: payload.groundworks.gardenTable || [],
          topsoilBalance: {
            totalStrippedVolume: 0,
            totalRequiredVolume: 0,
            balance: 0,
            importRequired: false,
            exportRequired: false,
            importCost: 0,
            exportCost: 0,
          },
          totals: {
            privateDrivesTotal: 0,
            sharedDrivesTotal: 0,
            pathsTotal: 0,
            gardensTotal: 0,
            topsoilAdjustmentTotal: 0,
            grandTotal: 0,
          },
        },
        privateDrainage: payload.groundworks.privateDrainage || {
          settings: {
            measurementMethod: "sampling_20_percent" as const,
            region: "southern" as const,
            samplingPercentage: 20,
            contingencyPercentage: 10,
            floorAreaFactor: {
              "100mm": 0.1,
              "150mm": 0.15,
              "225mm": 0.05,
            },
            fittingFactors: {
              bendsPerMeter: 1 / 6,
              junctionsPerMeter: 1 / 10,
              collarsPerMeter: 1 / 6,
              restBendsPerMeter: 1 / 12,
            },
            rates: {
              excavation: {
                shallow: 18.5,
                deep: 28.75,
                rock: 45.0,
              },
              pipes: {
                "100mm": 8.5,
                "150mm": 12.75,
                "225mm": 18.5,
              },
              fittings: {
                bends: { "100mm": 3.5, "150mm": 5.25, "225mm": 8.0 },
                junctions: { "100mm": 8.5, "150mm": 12.75, "225mm": 18.5 },
                collars: { "100mm": 2.25, "150mm": 3.5, "225mm": 5.25 },
                restBends: { "100mm": 4.75, "150mm": 7.25, "225mm": 11.5 },
              },
              chambers: {
                plastic_shallow: 125.0,
                plastic_deep: 185.0,
                concrete_shallow: 185.0,
                concrete_deep: 285.0,
              },
              connections: {
                stack_connection: 45.0,
                main_sewer_connection: 125.0,
              },
              bedding: 35.0,
              backfill: 12.5,
              compaction: 8.75,
            },
          },
          sampledPlots: payload.groundworks.drainageTable || [],
          summary: {
            totalPlots: 0,
            sampledPlots: 0,
            pipeQuantities: {},
            fittingQuantities: {
              bends: {},
              junctions: {},
              collars: {},
              restBends: {},
            },
            chamberQuantities: {},
            connectionQuantities: {},
            pipeCosts: {},
            fittingCosts: 0,
            chamberCosts: 0,
            connectionCosts: 0,
            excavationCosts: 0,
            totalCost: 0,
          },
          plotsNotMeasured: [],
          unusualValues: [],
          totals: {
            totalCost: 0,
            contingencyAmount: 0,
            grandTotal: 0,
          },
        },
        additionalItems: payload.groundworks.additionalItems || {
          retainingWalls: payload.groundworks.retainingWallsTable || [],
          attenuationCrates: [],
          compoundAreas: [],
          customCalculations: [],
          totals: {
            retainingWallsTotal: 0,
            attenuationCratesTotal: 0,
            compoundAreasTotal: 0,
            customCalculationsTotal: 0,
            grandTotal: 0,
          },
        },
        matrix: payload.groundworks.matrix || {
          settings: {
            projectType: "residential" as const,
            stages: [],
            defaultStages: {
              siteClearance: {
                id: "site_clearance",
                name: "Site Clearance",
                standardPercentage: 8,
                isActive: true,
                order: 1,
              },
              foundationExcavation: {
                id: "foundation_excavation",
                name: "Foundation Excavation",
                standardPercentage: 18,
                isActive: true,
                order: 2,
              },
              foundationConcrete: {
                id: "foundation_concrete",
                name: "Foundation Concrete",
                standardPercentage: 20,
                isActive: true,
                order: 3,
              },
              foundationBlockwork: {
                id: "foundation_blockwork",
                name: "Foundation Blockwork",
                standardPercentage: 12,
                isActive: true,
                order: 4,
              },
              foundationFloor: {
                id: "foundation_floor",
                name: "Foundation Floor",
                standardPercentage: 18,
                isActive: true,
                order: 5,
              },
              drainage: {
                id: "drainage",
                name: "Drainage",
                standardPercentage: 12,
                isActive: true,
                order: 6,
              },
              externalWorksDrives: {
                id: "external_works_drives",
                name: "External Works - Drives",
                standardPercentage: 8,
                isActive: true,
                order: 7,
              },
              externalWorksGardens: {
                id: "external_works_gardens",
                name: "External Works - Gardens",
                standardPercentage: 4,
                isActive: true,
                order: 8,
              },
            },
            cashFlowSettings: {
              enabled: false,
              monthlyBreakdown: false,
              programme: {
                startDate: new Date(),
                plotsPerMonth: 4,
                stageDelays: {},
              },
            },
          },
          plotData: [],
          stageTotals: {},
          projectTotals: {
            totalValue: 0,
            totalAdjustments: 0,
            adjustedTotal: 0,
          },
          exportOptions: {
            includeBreakdown: true,
            includeAdjustments: true,
            clientFriendly: false,
            format: "excel" as const,
          },
        },
        totals: payload.groundworks.totals || {
          foundationsTotal: 0,
          externalWorksTotal: 0,
          privateDrainageTotal: 0,
          additionalItemsTotal: 0,
          grandTotal: 0,
        },
        // Restore additional structures
        plotMatrix: payload.groundworks.plotMatrix || [],
        summarySheet: payload.groundworks.summarySheet,
        externalsSection: payload.groundworks.externalsSection,
        plotExternals: payload.groundworks.plotExternals || {},
        // Dynamic Bill Summary tabs and Cut & Fill's ad-hoc extra items live
        // outside `payload.groundworks` entirely — they're only ever
        // persisted under `billOfQuantities[0].groundworks.summary` (see
        // boqVersionUtils.ts/boqPayloadBuilder.ts's save side). Reconstructing
        // them here, in the single funnel every load path (dashboard Edit,
        // VersionHistory, loadTenderFromStorage) runs through, means
        // `state.groundworks` always carries them from the moment a project
        // loads — previously this only happened lazily inside
        // GroundworksReview.tsx's own hydration effects, so any save fired
        // before that page ever mounted (e.g. StepperNavigation's "Calculate
        // & Review" from another workflow step) saw them as `undefined` and
        // wiped them from the persisted BOQ, since that save is a
        // full-document overwrite.
        // Leave the key out entirely (rather than an empty array) when
        // nothing was persisted — GroundworksReview.tsx's own hydration
        // effect gates on `!state.groundworks?.dynamicBillSummaryTabs` to
        // decide whether its live, version-scoped fetch should (re)hydrate,
        // and an empty-but-present array would short-circuit that.
        ...((): { dynamicBillSummaryTabs?: ReturnType<typeof deserializeDynamicBillSummaryTabs> } => {
          const tabs = deserializeDynamicBillSummaryTabs(
            (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary,
          );
          return tabs.length > 0 ? { dynamicBillSummaryTabs: tabs } : {};
        })(),
        cutFillsExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.cutFills,
        ),
        // Foundations Bill Summary's own ad-hoc extra items — same
        // outside-`summary` situation as cutFillsExtraItems above.
        foundationsExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.foundations,
        ),
        // Drainage Bill Summary's own ad-hoc extra items — same
        // outside-`summary` situation as cutFillsExtraItems above.
        drainageExtraItems: deserializeDrainageExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.drainage,
        ),
        // Plot Drives Bill Summary's own ad-hoc extra items — same
        // outside-`summary` situation as cutFillsExtraItems above.
        plotDrivesExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.plotDrives,
        ),
        // Plot Service Bill Summary's own ad-hoc extra items — same
        // outside-`summary` situation as cutFillsExtraItems above.
        plotServiceExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.plotService,
        ),
        // Scaffold Bill Summary's own ad-hoc extra items — same
        // outside-`summary` situation as cutFillsExtraItems above.
        scaffoldExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.scaffold,
        ),
        // Plot Pavings Bill Summary's own ad-hoc extra items — same
        // outside-`summary` situation as cutFillsExtraItems above.
        plotPavingsExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.plotPavings,
        ),
        // Garden Bill Summary's own ad-hoc extra items — same
        // outside-`summary` situation as cutFillsExtraItems above.
        gardenExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.garden,
        ),
        // Ret & Screen Walls Bill Summary's own ad-hoc extra items — same
        // outside-`summary` situation as cutFillsExtraItems above.
        retainingWallsExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.retainingWalls,
        ),
        // Geowall & Tobermore Walls Bill Summary's own ad-hoc extra items —
        // same outside-`summary` situation as cutFillsExtraItems above.
        geoWallsExtraItems: deserializeCutFillsExtraItems(
          (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.groundworks?.summary?.geoWalls,
        ),
      }
    : undefined;

  // Extract global settings
  const pipeTypeMap: { [key: string]: "clay" | "plastic" | "concrete" } = {
    Plastic: "plastic",
    Clay: "clay",
    Concrete: "concrete",
  };
  const backfillTypeMap: { [key: string]: "type1" | "6F2" } = {
    "Type 1": "type1",
    "6F2": "6F2",
  };

  const pipeType =
    pipeTypeMap[
      payload.globalSettings?.roadsAndSewers?.defaultPipeType || "Plastic"
    ] || "plastic";
  const backfillType =
    backfillTypeMap[
      payload.globalSettings?.roadsAndSewers?.defaultBackfillType || "Type 1"
    ] || "type1";

  // Split region to extract siteAddress and region
  const regionValue = (payload.project?.region as any) || "West Midlands";
  const regionParts = regionValue.includes("~~")
    ? regionValue.split("~~")
    : [null, regionValue];
  const extractedSiteAddress = regionParts[0] || "";
  let extractedRegion = regionParts[1] || regionValue;

  // If region contains " - ", extract only the part after the dash
  // e.g., "Project - Yorkshire" -> "Yorkshire"
  if (extractedRegion && extractedRegion.includes(" - ")) {
    extractedRegion =
      extractedRegion.split(" - ").pop()?.trim() || extractedRegion;
  }

  return {
    id: existingState?.id || Date.now().toString(),
    projectName: payload.project?.name || "",
    client: payload.project?.client || "",
    siteAddress: extractedSiteAddress,
    region: extractedRegion,
    // boqStatus always takes precedence on load — status mirrors it.
    ...((): { status: string; boqStatus: string } => {
      const raw = (payload.boqStatus || payload.status) as string | undefined;
      const effective = !raw || raw === 'Pending' ? 'In-progress' : raw;
      return { status: effective as any, boqStatus: effective as any };
    })(),
    jobTypes: (() => {
      if (existingState?.jobTypes) return existingState.jobTypes;
      const jt = ((payload as unknown) as Record<string, unknown>)?.['job-types'] as { groundworks?: boolean; roadsAndSewers?: boolean } | undefined;
      if (jt && (jt.roadsAndSewers || jt.groundworks)) {
        return { roadsAndSewers: jt.roadsAndSewers === true, groundworks: jt.groundworks === true };
      }
      return { roadsAndSewers: false, groundworks: false };
    })(),
    activeJobType: existingState?.activeJobType || "roadsAndSewers",
    workflow: existingState?.workflow || {
      roadsAndSewers: {
        currentStep: 0,
        isComplete: false,
      },
      groundworks: {
        currentStep: 0,
        isComplete: false,
      },
    },
    globalSettings: {
      pipeType,
      backfillType,
      allowOverrides: false,
      roadsAndSewers: {
        overheadAndProfit:
          payload.globalSettings?.roadsAndSewers?.overheadAndProfit || 0,
        preliminaries:
          payload.globalSettings?.roadsAndSewers?.preliminaries || 0,
        discount: payload.globalSettings?.roadsAndSewers?.discount || 0,
      },
      groundworks: {
        overheadAndProfit:
          payload.globalSettings?.groundworks?.overheadAndProfit || 0,
        preliminaries: payload.globalSettings?.groundworks?.preliminaries || 0,
        discount: payload.globalSettings?.groundworks?.discount || 0,
      },
    },
    foulSewers: {
      manholes: asShaped(payload.roadsAndSewers?.foulSewers?.manholes, { table: [], config: {} }),
      pipeRuns: asShaped(payload.roadsAndSewers?.foulSewers?.pipeRuns, { table: [], config: {} }),
      lateralConnections: asShaped(
        payload.roadsAndSewers?.foulSewers?.lateralConnections,
        { table: [], config: { lateralsCount: 0, gulliesCount: 0 } },
      ),
    },
    pumpStation: asShaped(payload.roadsAndSewers?.pumpStation, { table: {}, config: {} }),
    risingMain: asShaped(payload.roadsAndSewers?.risingMain, { table: {}, config: {} }),
    stormSewers: {
      manholes: asShaped(payload.roadsAndSewers?.stormSewers?.manholes, { table: [], config: {} }),
      pipeRuns: asShaped(
        payload.roadsAndSewers?.stormSewers?.pipeRuns,
        { table: [], config: { roadGulliesCount: 0 } },
      ),
      lateralConnections: asShaped(payload.roadsAndSewers?.stormSewers?.lateralConnections, { table: [], config: {} }),
      gullyConnections: asShaped(
        payload.roadsAndSewers?.stormSewers?.gullyConnections,
        { table: [], config: { gullyConnectionLength: 6 } },
      ),
    },
    boxCulverts: asShaped(payload.roadsAndSewers?.boxCulverts, { table: {}, config: {} }),
    cellularAttenuation: asShaped(payload.roadsAndSewers?.cellularAttenuation, { table: {}, config: {} }),
    concAttenuation: asShaped(payload.roadsAndSewers?.concAttenuation, { table: {}, config: {} }),
    pondsSwales: asShaped(payload.roadsAndSewers?.pondsSwales, { table: {}, config: {} }),
    weholiteAttenuation: asShaped(payload.roadsAndSewers?.weholiteAttenuation, { table: {}, config: {} }),
    roads: {
      roadTypes: asShaped(payload.roadsAndSewers?.roads?.roadTypes, { table: [], config: {} }),
      cutFillSections: asShaped(
        payload.roadsAndSewers?.roads?.cutFillSections,
        { table: [], config: { stripValueDefault: 0 } },
      ),
      carriagewayFootways: asShaped(
        payload.roadsAndSewers?.roads?.carriagewayFootways,
        { table: [], config: { totalBuildUpValue: 0 } },
      ),
      capping: asShaped(
        payload.roadsAndSewers?.roads?.capping,
        { table: [], config: { cappingLayer: { enabled: false, thicknesses: [] } } },
      ),
      footpaths: asShaped(payload.roadsAndSewers?.roads?.footpaths, { table: [], config: {} }),
      manualQtys: (() => {
        const v = payload.roadsAndSewers?.roads?.manualQtys;
        return typeof v === 'object' && v !== null && !Array.isArray(v) ? v : {};
      })(),
    },
    mainServices: asShaped(payload.roadsAndSewers?.mainServices, { table: {}, config: {} }),
    substation: asShaped(payload.roadsAndSewers?.substation, { table: {}, config: {} }),
    pos: asShaped(payload.roadsAndSewers?.pos, { table: {}, config: {} }),
    compound: asShaped(payload.roadsAndSewers?.compound, { table: {}, config: {} }),
    spoilReconciliation: asShaped(
      payload.roadsAndSewers?.spoilReconciliation,
      { config: {} as SpoilReconciliationData },
      'config',
    ),
    // Carry the last-saved Sum Analysis TENDER TOTAL forward so a save triggered from any
    // page (not just Sum Analysis) keeps sending the real total instead of reverting to the
    // calculateAllTenderTotals estimate — re-seeded from where transformToPayload stores it.
    ...((): { roadsAndSewersTotals?: { grandTotal: number; calculatedAt: string } } => {
      const savedGrandTotal = (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.totals?.grandTotal;
      return typeof savedGrandTotal === "number"
        ? { roadsAndSewersTotals: { grandTotal: savedGrandTotal, calculatedAt: payload.lastModified || new Date().toISOString() } }
        : {};
    })(),
    // Roads & Sewers Dynamic Bill Summary tabs — same outside-any-domain-object
    // situation as groundworks' `dynamicBillSummaryTabs` above, persisted under
    // `billOfQuantities[0].roadAndSewers.summary` instead. Reconstructed here, in
    // the single funnel every load path runs through, so `state` always carries
    // them from the moment a project loads. Left out entirely (rather than an
    // empty array) when nothing was persisted — see the dynamicBillSummaryTabs
    // comment above for why that distinction matters to hydration effects.
    ...((): { roadsAndSewersDynamicBillSummaryTabs?: ReturnType<typeof deserializeRoadsAndSewersDynamicBillSummaryTabs> } => {
      const tabs = deserializeRoadsAndSewersDynamicBillSummaryTabs(
        (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary,
      );
      return tabs.length > 0 ? { roadsAndSewersDynamicBillSummaryTabs: tabs } : {};
    })(),
    // Ad-hoc user-added Section/Item rows for each fixed Roads & Sewers Bill
    // Summary (see tender.types.ts's own doc comment) — same outside-any-
    // domain-object situation as groundworks' `foundationsExtraItems` etc
    // above, persisted under `billOfQuantities[0].roadAndSewers.summary.<domain>`
    // instead of `groundworks.summary.<domain>`.
    prelimsExtraItems: deserializeCutFillsExtraItems(
      (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary?.prelims,
    ),
    foulSewersExtraItems: deserializeCutFillsExtraItems(
      (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary?.foulSewers,
    ),
    stormSewersExtraItems: deserializeCutFillsExtraItems(
      (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary?.stormSewers,
    ),
    roadsExtraItems: deserializeCutFillsExtraItems(
      (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary?.roads,
    ),
    mainServicesExtraItems: deserializeCutFillsExtraItems(
      (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary?.mainServices,
    ),
    substationExtraItems: deserializeCutFillsExtraItems(
      (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary?.substation,
    ),
    posExtraItems: deserializeCutFillsExtraItems(
      (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary?.pos,
    ),
    compoundExtraItems: deserializeCutFillsExtraItems(
      (payload as unknown as Record<string, any>).billOfQuantities?.[0]?.roadAndSewers?.summary?.compound,
    ),
    preliminaryItems: {
      autoCalculated: [],
      manualItems: payload.roadsAndSewers?.manualPreliminaryItems || [],
      mainServices: payload.roadsAndSewers?.mainServiceConnections || [],
      preliminaryItems: payload.roadsAndSewers?.preliminaryItems || [],
      pumpStation: null,
      totals: {
        autoCalculatedTotal: 0,
        manualItemsTotal: 0,
        mainServicesTotal: 0,
        pumpStationTotal: 0,
        grandTotal: 0,
      },
    },
    groundworks: groundworksData || {
      plots: [],
      foundations: [],
      cutFills: {
        paths: [],
        gardens: [],
        privateDrives: [],
        sharedDrives: [],
        drainageComponents: [],
        settings: {
          privateTarmacValue: 0,
          privateBlockValue: 0,
          sharedTarmacValue: 0,
          sharedBlockValue: 0,
        },
      },
      externalWorks: {
        settings: {
          gardenMeasurementMethod: "individual" as const,
          defaultTopsoilDepth: 150,
          standardDriveSpec: "standard_block_paving",
          topsoilStripDepth: 300,
          topsoilImportRate: 35.0,
          topsoilExportRate: 12.0,
          privateTarmacValue: 0,
          privateBlockValue: 0,
          sharedTarmacValue: 0,
          sharedBlockValue: 0,
        },
        specifications: [],
        privateDrives: [],
        sharedDrives: [],
        paths: [],
        gardens: [],
        topsoilBalance: {
          totalStrippedVolume: 0,
          totalRequiredVolume: 0,
          balance: 0,
          importRequired: false,
          exportRequired: false,
          importCost: 0,
          exportCost: 0,
        },
        totals: {
          privateDrivesTotal: 0,
          sharedDrivesTotal: 0,
          pathsTotal: 0,
          gardensTotal: 0,
          topsoilAdjustmentTotal: 0,
          grandTotal: 0,
        },
      },
      privateDrainage: {
        settings: {
          measurementMethod: "sampling_20_percent" as const,
          region: "southern" as const,
          samplingPercentage: 20,
          contingencyPercentage: 10,
          floorAreaFactor: {
            "100mm": 0.1,
            "150mm": 0.15,
            "225mm": 0.05,
          },
          fittingFactors: {
            bendsPerMeter: 1 / 6,
            junctionsPerMeter: 1 / 10,
            collarsPerMeter: 1 / 6,
            restBendsPerMeter: 1 / 12,
          },
          rates: {
            excavation: {
              shallow: 18.5,
              deep: 28.75,
              rock: 45.0,
            },
            pipes: {
              "100mm": 8.5,
              "150mm": 12.75,
              "225mm": 18.5,
            },
            fittings: {
              bends: { "100mm": 3.5, "150mm": 5.25, "225mm": 8.0 },
              junctions: { "100mm": 8.5, "150mm": 12.75, "225mm": 18.5 },
              collars: { "100mm": 2.25, "150mm": 3.5, "225mm": 5.25 },
              restBends: { "100mm": 4.75, "150mm": 7.25, "225mm": 11.5 },
            },
            chambers: {
              plastic_shallow: 125.0,
              plastic_deep: 185.0,
              concrete_shallow: 185.0,
              concrete_deep: 285.0,
            },
            connections: {
              stack_connection: 45.0,
              main_sewer_connection: 125.0,
            },
            bedding: 35.0,
            backfill: 12.5,
            compaction: 8.75,
          },
        },
        sampledPlots: [],
        summary: {
          totalPlots: 0,
          sampledPlots: 0,
          pipeQuantities: {},
          fittingQuantities: {
            bends: {},
            junctions: {},
            collars: {},
            restBends: {},
          },
          chamberQuantities: {},
          connectionQuantities: {},
          pipeCosts: {},
          fittingCosts: 0,
          chamberCosts: 0,
          connectionCosts: 0,
          excavationCosts: 0,
          totalCost: 0,
        },
        plotsNotMeasured: [],
        unusualValues: [],
        totals: {
          totalCost: 0,
          contingencyAmount: 0,
          grandTotal: 0,
        },
      },
      additionalItems: {
        retainingWalls: [],
        attenuationCrates: [],
        compoundAreas: [],
        customCalculations: [],
        totals: {
          retainingWallsTotal: 0,
          attenuationCratesTotal: 0,
          compoundAreasTotal: 0,
          customCalculationsTotal: 0,
          grandTotal: 0,
        },
      },
      matrix: {
        settings: {
          projectType: "residential" as const,
          stages: [],
          defaultStages: {
            siteClearance: {
              id: "site_clearance",
              name: "Site Clearance",
              standardPercentage: 8,
              isActive: true,
              order: 1,
            },
            foundationExcavation: {
              id: "foundation_excavation",
              name: "Foundation Excavation",
              standardPercentage: 18,
              isActive: true,
              order: 2,
            },
            foundationConcrete: {
              id: "foundation_concrete",
              name: "Foundation Concrete",
              standardPercentage: 20,
              isActive: true,
              order: 3,
            },
            foundationBlockwork: {
              id: "foundation_blockwork",
              name: "Foundation Blockwork",
              standardPercentage: 12,
              isActive: true,
              order: 4,
            },
            foundationFloor: {
              id: "foundation_floor",
              name: "Foundation Floor",
              standardPercentage: 18,
              isActive: true,
              order: 5,
            },
            drainage: {
              id: "drainage",
              name: "Drainage",
              standardPercentage: 12,
              isActive: true,
              order: 6,
            },
            externalWorksDrives: {
              id: "external_works_drives",
              name: "External Works - Drives",
              standardPercentage: 8,
              isActive: true,
              order: 7,
            },
            externalWorksGardens: {
              id: "external_works_gardens",
              name: "External Works - Gardens",
              standardPercentage: 4,
              isActive: true,
              order: 8,
            },
          },
          cashFlowSettings: {
            enabled: false,
            monthlyBreakdown: false,
            programme: {
              startDate: new Date(),
              plotsPerMonth: 4,
              stageDelays: {},
            },
          },
        },
        plotData: [],
        stageTotals: {},
        projectTotals: {
          totalValue: 0,
          totalAdjustments: 0,
          adjustedTotal: 0,
        },
        exportOptions: {
          includeBreakdown: true,
          includeAdjustments: true,
          clientFriendly: false,
          format: "excel" as const,
        },
      },
      totals: {
        foundationsTotal: 0,
        externalWorksTotal: 0,
        privateDrainageTotal: 0,
        additionalItemsTotal: 0,
        grandTotal: 0,
      },
    },
    calculationResults: null,
    dateCreated: payload.tenderCreatedDate
      ? new Date(payload.tenderCreatedDate)
      : new Date(),
    createdBy: payload.createdBy, // Preserve creator information
    opportunityNo: (payload as any).opportunityNo || undefined,
    opportunity: (payload as any).opportunity || undefined,
    lastModified: payload.lastModified
      ? new Date(payload.lastModified)
      : new Date(),
    currentStep: 0,
    isValid: false,
    isDirty: false,
  };
};

// Migration function to move manholes from sharedManholes to respective sewer types
const migrateTenderData = (data: any): TenderState => {
  // If this is version 1.0 or older, migrate the manholes
  if (data.sharedManholes && data.sharedManholes.manholes) {
    const sharedManholes = data.sharedManholes.manholes;

    // Separate manholes by type
    const foulManholes = sharedManholes.filter(
      (mh: any) => mh.type === "foul" || mh.type === "both"
    );
    const stormManholes = sharedManholes.filter(
      (mh: any) => mh.type === "storm" || mh.type === "both"
    );

    // Update foul sewers
    if (!data.foulSewers) data.foulSewers = {};
    data.foulSewers.manholes = foulManholes;
    if (!data.foulSewers.pipeRuns) data.foulSewers.pipeRuns = [];
    if (!data.foulSewers.lateralConnections)
      data.foulSewers.lateralConnections = [];
    if (!data.foulSewers.junctions) data.foulSewers.junctions = [];

    // Update storm sewers
    if (!data.stormSewers) data.stormSewers = {};
    data.stormSewers.manholes = stormManholes;
    if (!data.stormSewers.pipeRuns) data.stormSewers.pipeRuns = [];
    if (!data.stormSewers.lateralConnections)
      data.stormSewers.lateralConnections = [];
    if (!data.stormSewers.gullyConnections)
      data.stormSewers.gullyConnections = [];
    if (!data.stormSewers.junctions) data.stormSewers.junctions = [];

    // Remove the old sharedManholes property
    delete data.sharedManholes;
  }

  return data as TenderState;
};

export const saveTenderToStorage = (tender: TenderState): void => {
  try {
    // Transform to payload format (removes UI state)
    const payload = transformToPayload(tender);

    // The draft has no item-library data, so carry just the Plot Drives code picks in the
    // same `groundworksItemLibrary.plotDrives` shape the BOQ save uses; transformFromPayload
    // re-derives them from it on reload.
    const plotDrivesPicks = buildPlotDrivesLibraryFromOverrides(
      (tender.groundworks as { plotDrivesCodeOverrides?: Record<string, string> } | undefined)?.plotDrivesCodeOverrides,
    );
    const dataToSave = {
      version: STORAGE_VERSION,
      timestamp: new Date().toISOString(),
      data: plotDrivesPicks ? { ...payload, groundworksItemLibrary: { plotDrives: plotDrivesPicks } } : payload,
    };
    // Save with project-specific key if project name exists
    const storageKey = tender.projectName
      ? `mjEvans_tender_${tender.id}`
      : STORAGE_KEY;
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));

    // Also update currentTender to ensure it's always in sync
    // This fixes the issue where page reload loads stale data from currentTender
    localStorage.setItem("currentTender", JSON.stringify(dataToSave));
  } catch (error) {
    console.error("Failed to save tender to localStorage:", error);
  }
};

export const loadTenderFromStorage = (): TenderState | null => {
  try {
    // PRIORITY 1: Check for "currentTender" - this is set when loading a specific version
    const currentTender = localStorage.getItem("currentTender");
    if (currentTender) {
      try {
        const parsed = JSON.parse(currentTender);
        if (parsed.version === STORAGE_VERSION && parsed.data) {
          return transformFromPayload(parsed.data as PayloadFormat);
        }
      } catch (e) {
        console.error("Error loading currentTender:", e);
      }
    }

    // PRIORITY 2: Check for the most recent project from all saved projects
    const allProjects = getAllSavedProjects();

    // If there are saved projects, load the most recent one
    if (allProjects.length > 0) {
      const mostRecentProject = allProjects[0]; // Already sorted by lastModified desc
      return loadSpecificProject(mostRecentProject.id);
    }

    // PRIORITY 3: Fall back to checking the generic draft key
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Handle version migration
    let migratedData = parsed.data;

    if (parsed.version === "1.0" || parsed.version === "2.0") {
      // Old format - convert to new payload format first, then to TenderState
      migratedData = migrateTenderData(parsed.data);
      const migratedTender = transformFromPayload(
        transformToPayload(migratedData)
      );
      saveTenderToStorage(migratedTender);
      return migratedTender;
    } else if (parsed.version === STORAGE_VERSION) {
      // New payload format - transform to TenderState
      return transformFromPayload(parsed.data as PayloadFormat);
    } else {
      clearTenderFromStorage();
      return null;
    }
  } catch (error) {
    console.error("Failed to load tender from localStorage:", error);
    return null;
  }
};

export const clearTenderFromStorage = (tenderId?: string): void => {
  try {
    // Remove generic draft key
    localStorage.removeItem(STORAGE_KEY);

    // If tender ID provided, also remove project-specific key
    if (tenderId) {
      const projectKey = `mjEvans_tender_${tenderId}`;
      localStorage.removeItem(projectKey);
    }
  } catch (error) {
    console.error("Failed to clear tender from localStorage:", error);
  }
};

export const exportTenderAsJSON = (tender: TenderState): void => {
  try {
    const dataStr = JSON.stringify(tender, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

    const exportFileDefaultName = `mj-evans-tender-${
      tender.projectName || "draft"
    }-${new Date().toISOString().split("T")[0]}.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  } catch (error) {
    console.error("Failed to export tender as JSON:", error);
  }
};

export const importTenderFromJSON = (file: File): Promise<TenderState> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const result = event.target?.result;
        if (typeof result === "string") {
          const tender = JSON.parse(result) as TenderState;
          tender.lastModified = new Date(tender.lastModified);
          resolve(tender);
        } else {
          reject(new Error("Invalid file content"));
        }
      } catch (error) {
        reject(new Error("Failed to parse JSON file"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
};

export const getAllSavedProjects = (): Array<{
  id: string;
  projectName: string;
  client: string;
  siteAddress?: string;
  region: string;
  dateCreated: Date;
  lastModified: Date;
}> => {
  try {
    const projects: Array<{
      id: string;
      projectName: string;
      client: string;
      siteAddress?: string;
      region: string;
      dateCreated: Date;
      lastModified: Date;
    }> = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("mjEvans_tender_")) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            const tenderData = parsed.data || parsed;

            // Handle both old format (direct properties) and new format (nested in project)
            let projectName: string;
            let client: string;
            let siteAddress: string | undefined;
            let region: string;
            let dateCreated: Date;
            let lastModified: Date;
            let id: string;

            if (parsed.version === STORAGE_VERSION && tenderData.project) {
              // New payload format
              projectName = tenderData.project.name || "";
              client = tenderData.project.client || "";

              // Extract siteAddress and region from combined region field
              const regionValue = tenderData.project.region || "West Midlands";
              if (regionValue.includes("~~")) {
                const parts = regionValue.split("~~");
                siteAddress = parts[0] || undefined;
                region = parts[1] || "West Midlands";
              } else {
                siteAddress = undefined;
                region = regionValue;
              }

              // If region contains " - ", extract only the part after the dash
              // e.g., "Project - Yorkshire" -> "Yorkshire"
              if (region && region.includes(" - ")) {
                region = region.split(" - ").pop()?.trim() || region;
              }

              dateCreated = new Date(
                tenderData.tenderCreatedDate || tenderData.lastModified
              );
              lastModified = new Date(tenderData.lastModified);
              // Extract ID from storage key
              id = key.replace("mjEvans_tender_", "");
            } else {
              // Old format
              projectName = tenderData.projectName || "";
              client = tenderData.client || "";
              siteAddress = tenderData.siteAddress || undefined;
              region = tenderData.region || "West Midlands";
              dateCreated = new Date(
                tenderData.dateCreated || tenderData.lastModified
              );
              lastModified = new Date(tenderData.lastModified);
              id = tenderData.id || key.replace("mjEvans_tender_", "");
            }

            if (projectName && id) {
              projects.push({
                id,
                projectName,
                client,
                siteAddress,
                region,
                dateCreated,
                lastModified,
              });
            }
          } catch (error) {
            // Failed to parse tender
          }
        }
      }
    }

    return projects.sort(
      (a, b) => b.lastModified.getTime() - a.lastModified.getTime()
    );
  } catch (error) {
    console.error("Failed to load saved projects:", error);
    return [];
  }
};

/**
 * Trigger immediate save after data import
 * This ensures imported data is persisted to localStorage immediately
 * Adds a small delay to ensure React state updates have completed
 *
 * ============================================================
 * AUTO-SAVE DISABLED - Function body commented out for now
 * ============================================================
 */
export const triggerImmediateSave = (recordCount: number = 0): void => {
  // if (typeof window !== "undefined") {
  //   // Add delay to ensure updateField dispatch has completed
  //   setTimeout(() => {
  //     window.dispatchEvent(
  //       new CustomEvent("excel-import-complete", {
  //         detail: { recordCount },
  //       })
  //     );
  //   }, 100);
  // }
};

/**
 * Prepare tender data for API submission
 * Converts TenderState to PayloadFormat for API calls
 */
export const prepareTenderForAPI = (
  tender: TenderState,
  createdBy?: string
): PayloadFormat => {
  return transformToPayload(tender, createdBy);
};

/**
 * Process tender data received from API
 * Converts PayloadFormat from API to TenderState
 */
export const processTenderFromAPI = (
  payload: PayloadFormat,
  existingState?: Partial<TenderState>
): TenderState => {
  return transformFromPayload(payload, existingState);
};

export const loadSpecificProject = (projectId: string): TenderState | null => {
  try {
    const storageKey = `mjEvans_tender_${projectId}`;
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Handle version migration
    let migratedData = parsed.data;

    if (parsed.version === "1.0" || parsed.version === "2.0") {
      // Old format - convert to new payload format first, then to TenderState
      migratedData = migrateTenderData(parsed.data);
      const migratedTender = transformFromPayload(
        transformToPayload(migratedData),
        { id: projectId }
      );
      saveTenderToStorage(migratedTender);
      return migratedTender;
    } else if (parsed.version === STORAGE_VERSION) {
      // New payload format - transform to TenderState
      return transformFromPayload(parsed.data as PayloadFormat, {
        id: projectId,
      });
    } else {
      return null;
    }
  } catch (error) {
    console.error("Failed to load specific project from localStorage:", error);
    return null;
  }
};
